/**
 * One line and one main button in Aftaledetaljer → Betaling.
 * Secondary actions stay under Flere muligheder.
 */
export type BetalingPhase = 'unfinished' | 'unpaid' | 'overdue' | 'paid' | 'refunded';

export type BetalingPrimary = 'finish' | 'record' | 'remind' | 'none';

export type BetalingMoreAction = 'credit' | 'refund' | 'record_payment' | 'use_clip' | 'override' | 'resend_invoice';

const CLOSED = new Set(['completed', 'cancelled', 'no_show']);

export function appointmentBetalingPhase(input: {
  appointmentStatus: string | null | undefined;
  invoiceStatus?: string | null;
  visitPayment: string;
}): BetalingPhase {
  const status = input.appointmentStatus ?? '';
  if (!CLOSED.has(status)) return 'unfinished';
  const invoice = input.invoiceStatus ?? '';
  if (invoice === 'refunded' || input.visitPayment === 'refunded') return 'refunded';
  if (invoice === 'overdue') return 'overdue';
  if (
    invoice === 'paid' ||
    input.visitPayment === 'paid' ||
    input.visitPayment === 'forfeited' ||
    input.visitPayment === 'credited'
  ) {
    return 'paid';
  }
  return 'unpaid';
}

export function betalingPrimary(phase: BetalingPhase, hasInvoice: boolean): BetalingPrimary {
  switch (phase) {
    case 'unfinished':
      return 'finish';
    case 'unpaid':
      return hasInvoice ? 'record' : 'none';
    case 'overdue':
      return 'remind';
    case 'paid':
    case 'refunded':
      return 'none';
    default: {
      const unexpected: never = phase;
      return unexpected;
    }
  }
}

export function betalingMore(input: {
  phase: BetalingPhase;
  hasInvoice: boolean;
  allowOverride: boolean;
  canUseClip: boolean;
}): BetalingMoreAction[] {
  const actions: BetalingMoreAction[] = [];
  switch (input.phase) {
    case 'unfinished':
      break;
    case 'unpaid':
      if (input.hasInvoice) {
        actions.push('credit');
        if (input.canUseClip) actions.push('use_clip');
      }
      break;
    case 'overdue':
      actions.push('record_payment', 'credit');
      break;
    case 'paid':
      actions.push('refund', 'credit');
      break;
    case 'refunded':
      break;
    default: {
      const unexpected: never = input.phase;
      return unexpected;
    }
  }
  if (input.allowOverride && input.phase !== 'refunded' && input.phase !== 'unfinished') {
    actions.push('override');
  }
  return actions;
}

export function betalingShowsSendMenu(phase: BetalingPhase, hasInvoice: boolean): 'beside' | 'more' | 'hidden' {
  if (!hasInvoice) return 'hidden';
  if (phase === 'unpaid') return 'beside';
  if (phase === 'overdue') return 'more';
  return 'hidden';
}

export function betalingShowsInvoiceLink(phase: BetalingPhase, hasInvoice: boolean): boolean {
  return hasInvoice && (phase === 'unpaid' || phase === 'overdue' || phase === 'paid');
}

export function betalingSummary(input: {
  phase: BetalingPhase;
  visitPayment: string;
  invoiceNumber: string | null;
  method: string | null;
  paidOn: string | null;
  creditNoteNumber: string | null;
  unpaid: string;
  prepaid: string;
  unpaidInvoice: (number: string) => string;
  overdue: string;
  paid: (method: string, date: string) => string;
  refunded: (number: string | null) => string;
}): string {
  switch (input.phase) {
    case 'unfinished':
      return input.visitPayment === 'prepaid' || input.visitPayment === 'partly_paid'
        ? input.prepaid
        : input.unpaid;
    case 'unpaid':
      return input.invoiceNumber ? input.unpaidInvoice(input.invoiceNumber) : input.unpaid;
    case 'overdue':
      return input.overdue;
    case 'paid':
      return input.paid(input.method || '—', input.paidOn || '—');
    case 'refunded':
      return input.refunded(input.creditNoteNumber);
    default: {
      const unexpected: never = input.phase;
      return unexpected;
    }
  }
}

/** Copenhagen calendar day, dd.mm.åååå. */
export function danishPaymentDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat('da-DK', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    timeZone: 'Europe/Copenhagen',
  }).format(date);
}
